"use client";

import type { Coords, MapPlace } from "@/types/places";
import { useTranslations } from "next-intl";
import { useCallback } from "react";
import CustomMapMarker from "./CustomMapMarker";
import GoogleMapBase from "./GoogleMapBase";

interface DetailPageMapProps {
  places: MapPlace[];
  currentLocation: Coords | null;
  googleMapsApiKey?: string;
}

/**
 * Map component for detail pages (club/event) with custom marker rendering
 */
export default function DetailPageMap({
  places,
  currentLocation,
  googleMapsApiKey,
}: DetailPageMapProps) {
  const t = useTranslations();

  const renderMapMarker = useCallback(
    (
      place: MapPlace,
      isHovered: boolean,
      isActive: boolean,
      onMouseEnter: () => void,
      onMouseLeave: () => void,
    ) => {
      const typeLabel =
        place.type === "event" ? t("clustering.event") : t("clustering.club");
      const ariaLabel = t("clustering.markerLabelSingle", {
        type: typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1),
        name: place.name,
      });

      return (
        <CustomMapMarker
          place={place}
          isHovered={isHovered}
          isActive={isActive}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          ariaLabel={ariaLabel}
        />
      );
    },
    [t],
  );

  return (
    <GoogleMapBase
      places={places}
      currentLocation={currentLocation}
      googleMapsApiKey={googleMapsApiKey}
      renderMarker={renderMapMarker}
      showCurrentLocation={false}
    />
  );
}
